import { useState, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Sidebar from './Sidebar';
import Header from './Header';
import ActingAsBanner from '../family/ActingAsBanner';
import TenantPlanBanner from './TenantPlanBanner';
import { fetchFeatureFlags } from '../../redux/featureSlice';
import { getDoctorAccess, getSubscriptionStatus, isSubscriptionLocked } from '../../redux/subscriptionSlice';
import SubscriptionLockModal from '../modals/SubscriptionLockModal';
import FloatingAssistant from '../assistant/FloatingAssistant';
import MocLinkSocketBridge from '../moc/MocLinkSocketBridge';
import VisitShareSocketBridge from '../health/VisitShareSocketBridge';

export default function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const location = useLocation();
  const mainRef = useRef(null);
  const sidebarRef = useRef(null);
  const dispatch = useDispatch();
  const featureLoaded = useSelector((state) => state.feature?.loaded);

  // Load admin-controlled feature flags once per session. The Sidebar and the
  // appointment modals read from this slice to gate role-specific items, so
  // it has to be in place before those components render their conditionals.
  useEffect(() => {
    if (!featureLoaded) {
      dispatch(fetchFeatureFlags());
    }
  }, [dispatch, featureLoaded]);

  // Load the doctor's resolved access + subscription status once per session, so
  // features can be gated by their package and an expired plan locks the app.
  useEffect(() => {
    const role = (localStorage.getItem('Role') || '').toLowerCase();
    const id = localStorage.getItem('ID');
    if (role === 'doctor' && id) {
      dispatch(getDoctorAccess(id));
      dispatch(getSubscriptionStatus(id));
    }
  }, [dispatch]);

  // Expired doctor (no grace) → block the app everywhere except the renew page.
  const role = (localStorage.getItem('Role') || '').toLowerCase();
  const locked = useSelector(isSubscriptionLocked);
  const showLock = role === 'doctor' && locked && location.pathname !== '/mySubscription';

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 1024) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        window.innerWidth <= 1024 &&
        sidebarOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target)
      ) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sidebarOpen]);

  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <div data-testid="app-layout" className={`flex h-screen ${window.innerWidth <= 1024 ? 'pb-14' : ''}`}>
      <Sidebar
        ref={sidebarRef}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <div data-testid="app-main-content" className="flex flex-col flex-1 overflow-hidden">
        <Header onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
        {/* Family accounts: says loudly when the login is acting as someone
            other than the account holder. Renders nothing otherwise. Placed
            above <main> so it is visible on every screen — booking on the wrong
            profile is the mistake this prevents. */}
        <ActingAsBanner />
        {/* The organisation's plan from Mitra One Care: "ends on …" for the
            admin from 15 days before; "ended on …" for everyone when the
            plan ended with "Message only" (doc/5.10.2026.md). */}
        <TenantPlanBanner />
        <main
          data-testid="app-main"
          ref={mainRef}
          className={`flex-1 overflow-auto custom-scrollbar bg-gradient-to-r from-gray-100 to-teal-50 ${window.innerWidth <= 1024 ? 'p-0' : 'p-4'}`}
        >
          <Outlet />
        </main>
      </div>
      {/* MOC linking: toast + list refresh when a patient answers an
          Approval-channel link request in their app (admin/staff/doctor). */}
      <MocLinkSocketBridge />
      {/* Visit sharing: a patient shared / answered an ask / revoked (doctor). */}
      <VisitShareSocketBridge />
      {/* Floating AI Assistant (doctor + patient) */}
      <FloatingAssistant />
      {showLock && <SubscriptionLockModal />}
    </div>
  );
}